import { Command as CommandPrimitive } from "cmdk";
import * as React from "react";

import { faCheck } from "@performyard/fontawesome/pro-regular-svg-icons";

import { Option } from "../../../../../types/Option";

import { cn } from "../../../../../lib/utils";
import { getDefaultOptionDisplay } from "../../utils/optionDisplay";
import { getOptionKey } from "../../utils/optionKey";

import { Icon } from "../../../../atoms/Icon";

type SelectOptionItemProps<T> = {
	option: Option<T>;
	isSelected: boolean;
	isFocused?: boolean;
	onSelect: () => void;
	renderOption?: (option: Option<T>, isSelected: boolean) => React.ReactNode;
	virtualItem?: {
		index: number;
		size: number;
		start: number;
	};
	onMouseEnter?: () => void;
	onMouseLeave?: () => void;
	showCheckmark?: boolean;
	virtualizationConfig?: {
		itemHeight?: number;
		maxHeight?: number;
	};
};

export const SelectOptionItem = <T,>({
	option,
	isSelected,
	isFocused = false,
	onSelect,
	renderOption,
	virtualItem,
	onMouseEnter,
	onMouseLeave,
	showCheckmark = false,
	virtualizationConfig,
}: SelectOptionItemProps<T>) => {
	const uniqueKey = getOptionKey(option);
	const optionDisplay = getDefaultOptionDisplay(option, renderOption, isSelected);

	const className = cn(
		virtualItem
			? "absolute left-0 top-0 flex w-full cursor-pointer select-none items-center px-3 text-paragraph-3 outline-none transition-colors hover:bg-neutral-1 focus:bg-neutral-1 data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 min-w-0"
			: "flex w-full cursor-pointer select-none items-center px-3 text-paragraph-3 outline-none transition-colors hover:bg-neutral-1 focus:bg-neutral-1 data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 min-w-0",
		(isSelected || isFocused) && "bg-neutral-1"
	);

	const style = virtualItem
		? {
				height: `${virtualItem.size}px`,
				transform: `translateY(${virtualItem.start}px)`,
			}
		: {
				height: `${virtualizationConfig?.itemHeight ?? 32}px`,
			};

	const handleSelect = () => {
		if (!option.disabled) {
			onSelect();
		}
	};

	const handleClick = () => {
		if (!option.disabled) {
			onSelect();
		}
	};

	return (
		<CommandPrimitive.Item
			value={uniqueKey}
			onSelect={handleSelect}
			onClick={handleClick}
			disabled={option.disabled}
			className={className}
			style={style}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
		>
			<div className="pointer-events-none flex min-w-0 flex-1 items-center">{optionDisplay}</div>
			{showCheckmark && isSelected && (
				<div className="pointer-events-none ml-auto">
					<Icon icon={faCheck} size={16} color="neutral-6" />
				</div>
			)}
		</CommandPrimitive.Item>
	);
};
